import React, {RefObject} from 'react';
import {Button} from '@tickchak/react-share/app';
import * as css from './AIMessageGenerator.css';
import GeneratorChatMessage from './generatorChatMessage/GeneratorChatMessage';
import GeneratorSuggestions from './generatorSuggestions/GeneratorSuggestions';
import CustomScrollbar from '../customScrollbar/CustomScrollbar';
import AiGenerate from '../../assets/icons/aiGenerate';
import type {AssistantDraftPayload, ChatMessage} from './utils/types';
import type {TXHR} from '../../assets/constants/constansts.type';

interface AIMessageGeneratorPanelProps {
    messages: ChatMessage[];
    suggestions: string[];
    promptText: string;
    placeholder: string;
    draftLoading: boolean;
    approveLoading: boolean;
    approveLabel: string;
    submitLabel: string;
    submitXhrState?: TXHR;
    error: string | null;
    isMobile: boolean;
    disableSubmit: boolean;
    scrollColumnRef: RefObject<HTMLDivElement>;
    promptTextareaRef: RefObject<HTMLTextAreaElement>;
    onSuggestionClick: (text: string, index: number) => void;
    onApprove: (draft: AssistantDraftPayload) => void;
    onPromptChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onPromptKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    onSubmitPrompt: () => void;
    innerPop?: boolean;
}

function AIMessageGeneratorPanel({
    messages,
    suggestions,
    promptText,
    placeholder,
    draftLoading,
    approveLoading,
    approveLabel,
    submitLabel,
    submitXhrState,
    error,
    isMobile,
    disableSubmit,
    scrollColumnRef,
    promptTextareaRef,
    onSuggestionClick,
    onApprove,
    onPromptChange,
    onPromptKeyDown,
    onSubmitPrompt,
    innerPop = false,
}: AIMessageGeneratorPanelProps) {
    const PanelInner = innerPop ? css.innerPopPanelInner : css.panelInner;

    return (
        <PanelInner>
            <css.scrollArea>
                {!isMobile && (
                    <CustomScrollbar
                        contentRef={scrollColumnRef}
                        cssStyle="inset-inline-end: 3px;"
                        maxBottom={65}
                    />
                )}
                <css.scrollColumn ref={scrollColumnRef}>
                    {messages.length === 0 && (
                        <GeneratorSuggestions
                            suggestions={suggestions}
                            onSuggestionClick={onSuggestionClick}
                        />
                    )}
                    {(messages.length > 0 || draftLoading) && (
                        <css.chatColumn>
                            {messages.map((message, index) => (
                                <GeneratorChatMessage
                                    key={index}
                                    message={message}
                                    index={index}
                                    messages={messages}
                                    draftLoading={draftLoading}
                                    approveLabel={approveLabel}
                                    approveLoading={approveLoading}
                                    onApprove={onApprove}
                                />
                            ))}
                        </css.chatColumn>
                    )}
                </css.scrollColumn>
                {error && (
                    <css.errorText role="alert" data-testid="ai-message-generator-error">
                        {error}
                    </css.errorText>
                )}
            </css.scrollArea>
            <css.promptArea>
                <css.promptTextarea
                    ref={promptTextareaRef}
                    value={promptText}
                    onChange={onPromptChange}
                    onKeyDown={onPromptKeyDown}
                    placeholder={placeholder}
                    data-testid="ai-message-generator-prompt"
                    rows={1}
                />
                <Button
                    disabled={disableSubmit}
                    onClick={onSubmitPrompt}
                    data-testid="ai-message-generator-submit"
                    iconEnd={<AiGenerate />}
                    size="category"
                    variant="brand"
                    xhrState={submitXhrState}
                >
                    {submitLabel}
                </Button>
            </css.promptArea>
        </PanelInner>
    );
}

export default AIMessageGeneratorPanel;
